/**
 * attention-rail.tsx — Role-aware "Needs your attention" package rail.
 *
 * Filters enriched packages by role and currentUserId:
 *
 *   admin  — blockerCount > 0 | lifecycle === 'blocked' | reviewer === currentUser
 *   editor — ownerUserId === currentUser | reviewer === currentUser
 *   viewer — reviewer === currentUser only
 *   agent  — same as viewer
 *
 * This mapping is surfaced in the PR body so the reviewer can adjust
 * semantics without needing a code change. See phase-01 risk table.
 *
 * Props are pure — no data fetching here. HomeDashboard owns the query.
 */

import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { cn } from '../../lib/utils'
import { useAppStore } from '../../stores/app-store'
import { AlertTriangle } from 'lucide-react'
import type { OnboardingPackage } from '@agentwiki/shared'

// ── Types ─────────────────────────────────────────────────────────────────────

/** Matches the actual tenantMemberships.role enum in the codebase. */
export type AuthRole = 'admin' | 'editor' | 'viewer' | 'agent'

export interface AttentionRailProps {
  packages: OnboardingPackage[]
  role: AuthRole
  currentUserId: string
}

// ── Filter logic ──────────────────────────────────────────────────────────────

function filterByRole(
  packages: OnboardingPackage[],
  role: AuthRole,
  currentUserId: string,
): OnboardingPackage[] {
  switch (role) {
    case 'admin':
      // All blocked packages + packages with any blocker + reviews assigned to current user
      return packages.filter(
        (p) =>
          p.lifecycle === 'blocked' ||
          (p.trust?.blockerCount ?? 0) > 0 ||
          p.assignedReviewerUserId === currentUserId,
      )
    case 'editor':
      // Packages they own (createdBy or owner) + reviews assigned to current user
      return packages.filter(
        (p) =>
          p.ownerUserId === currentUserId ||
          p.assignedReviewerUserId === currentUserId,
      )
    case 'viewer':
    case 'agent':
    default:
      // Only packages where current user is the assigned reviewer
      return packages.filter((p) => p.assignedReviewerUserId === currentUserId)
  }
}

// ── Sub-component: single attention row ───────────────────────────────────────

interface AttentionRowProps {
  pkg: OnboardingPackage
  isDark: boolean
}

function AttentionRow({ pkg, isDark }: AttentionRowProps) {
  const isBlocked =
    pkg.lifecycle === 'blocked' || (pkg.trust?.blockerCount ?? 0) > 0

  return (
    <li>
      <Link
        to={`/packages/${pkg.id}`}
        className={cn(
          'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors',
          isDark
            ? 'text-neutral-300 hover:bg-surface-3'
            : 'text-neutral-700 hover:bg-neutral-100',
        )}
      >
        {isBlocked && (
          <AlertTriangle
            className={cn(
              'h-3.5 w-3.5 shrink-0',
              isDark ? 'text-red-400' : 'text-red-500',
            )}
            aria-hidden="true"
          />
        )}
        <span className="truncate">{pkg.name}</span>
        {pkg.lifecycle === 'blocked' && (
          <span
            className={cn(
              'ml-auto shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide',
              isDark
                ? 'bg-red-500/20 text-red-300'
                : 'bg-red-100 text-red-700',
            )}
          >
            blocked
          </span>
        )}
      </Link>
    </li>
  )
}

// ── Component ─────────────────────────────────────────────────────────────────

export function AttentionRail({ packages, role, currentUserId }: AttentionRailProps) {
  const { t } = useTranslation('dashboard')
  const { theme } = useAppStore()
  const isDark = theme === 'dark'

  const filtered = filterByRole(packages, role, currentUserId)

  return (
    <section aria-label={t('attention.title')}>
      <h2
        className={cn(
          'mb-2 text-xs font-semibold uppercase tracking-wide',
          isDark ? 'text-neutral-500' : 'text-neutral-400',
        )}
      >
        {t('attention.title')}
      </h2>

      {filtered.length === 0 ? (
        <p className={cn('py-4 text-sm', isDark ? 'text-neutral-500' : 'text-neutral-500')}>
          {t('attention.empty')}
        </p>
      ) : (
        <ul className="space-y-0.5" role="list">
          {filtered.map((pkg) => (
            <AttentionRow key={pkg.id} pkg={pkg} isDark={isDark} />
          ))}
        </ul>
      )}
    </section>
  )
}
